// Wearing, keeping and sharing themes. A board wears a built-in place by its
// id, or a palette or made theme through theme.custom. Changing it is one
// undoable step, and the change plays as a wipe across the screen.

import type { CustomTheme, SceneSpec, ThemeBackground, ThemeColors, ThemeEffect, ThemeId } from "../../shared/types";
import { cleanTheme } from "../../shared/themeSpec";
import { api } from "../lib/api";
import { THEME, madeTheme, setMadeThemes } from "../themes/themes";
import { markThemed } from "../chrome/Corner";
import { withWipe } from "../lib/wipe";
import { updateTheme } from "./actions";
import { currentBoard, get, set } from "./store";

/** Puts a theme on the board in view: a built-in place, a palette or a made theme. */
export function wearTheme(id: string, opts: { wipe?: boolean } = {}) {
  const b = currentBoard(get());
  if (!b) return;
  const apply = () => {
    if (Object.hasOwn(THEME, id)) updateTheme({ id: id as ThemeId, custom: null, accent: null, backdrop: null, font: null, opacity: null, blur: null, bg: null });
    // a made theme comes as it was made: the board's own tweaks give way
    else updateTheme({ custom: id, accent: null, backdrop: null, font: null, opacity: null, blur: null, radius: null, bg: null });
    markThemed();
  };
  if (opts.wipe === false) apply();
  else withWipe(apply);
}

/** The id the board in view wears. */
export function wornTheme(): string {
  const b = currentBoard(get());
  return b?.theme.custom ?? b?.theme.id ?? "meadow";
}

function keep(t: CustomTheme) {
  const themes = { ...get().themes, [t.id]: t };
  setMadeThemes(Object.values(themes));
  set({ themes });
}

export async function saveTheme(spec: unknown, opts: { apply?: boolean; by?: string } = {}): Promise<CustomTheme> {
  const r = await api<{ theme: CustomTheme }>("/api/themes", { body: { theme: spec, apply: false, by: opts.by } });
  keep(r.theme);
  if (opts.apply) wearTheme(r.theme.id);
  return r.theme;
}

export async function replaceTheme(id: string, spec: unknown, ifVersion?: number): Promise<CustomTheme> {
  const r = await api<{ theme: CustomTheme }>(`/api/themes/${encodeURIComponent(id)}`, { method: "PUT", body: { theme: spec, ...(ifVersion !== undefined ? { ifVersion } : {}) } });
  keep(r.theme);
  return r.theme;
}

export async function removeTheme(id: string) {
  await api(`/api/themes/${encodeURIComponent(id)}`, { method: "DELETE" });
  const themes = { ...get().themes };
  delete themes[id];
  setMadeThemes(Object.values(themes));
  set({ themes });
}

/** What travels when a theme is shared: the look, never its id or who wore it. */
export interface ThemeShare {
  name: string;
  blurb: string;
  by: string | null;
  mode: "dark" | "light";
  font: CustomTheme["font"];
  colors: ThemeColors;
  glass?: number;
  blur?: number;
  radius?: number;
  scene: SceneSpec;
  /** the pictures go by the board's own keys, so a shared theme shows them on any board */
  backgrounds?: ThemeBackground[];
  effects?: ThemeEffect[];
  pixel?: boolean;
  /** a drawing travels only until it has become the theme's picture; after that the picture carries it */
  drawing?: string;
}

export function shareOf(t: CustomTheme): ThemeShare {
  return {
    name: t.name,
    blurb: t.blurb,
    by: t.by,
    mode: t.mode,
    font: t.font,
    colors: t.colors,
    glass: t.glass,
    blur: t.blur,
    radius: t.radius,
    scene: t.scene,
    ...(t.backgrounds?.length ? { backgrounds: t.backgrounds } : {}),
    ...(t.effects ? { effects: t.effects } : {}),
    ...(t.pixel ? { pixel: true } : {}),
    ...(t.drawing && !t.backgrounds?.length ? { drawing: t.drawing } : {}),
  };
}

/** The whole of a theme as it is saved again: what a share carries, and its drawing, kept as the picture's source. */
export function specOf(t: CustomTheme): ThemeShare {
  return { ...shareOf(t), ...(t.drawing ? { drawing: t.drawing } : {}) };
}

/** Shows the next of the worn theme's backgrounds, the way B does, a built-in picture theme's or a made one's. False when it has fewer than two. */
export function nextBackground(step = 1): boolean {
  const b = currentBoard(get());
  // the built-in picture themes live with the code and the workspace's own in the store: madeTheme knows both
  const pics = madeTheme(b?.theme.custom)?.backgrounds;
  if (!b || !pics || pics.length < 2) return false;
  updateTheme({ bg: ((((b.theme.bg ?? 0) + step) % pics.length) + pics.length) % pics.length });
  return true;
}

const toB64 = (s: string) => btoa(unescape(encodeURIComponent(s))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const fromB64 = (s: string) => decodeURIComponent(escape(atob(s.replace(/-/g, "+").replace(/_/g, "/"))));

/**
 * A link that carries the whole theme in its fragment, so it works on any
 * board and never passes through anyone's server. Opening it asks first.
 */
export function themeLink(t: CustomTheme): string {
  return `${location.origin}/#theme=${toB64(JSON.stringify(shareOf(t)))}`;
}

/**
 * A shared theme from a link, the text of one, or its JSON: checked like any
 * other theme, or null. It never throws: a link that cannot be read, whatever
 * is in it, is a link that did not open.
 */
export function readShared(text: string): ThemeShare | null {
  try {
    const s = text.trim();
    const frag = /[#&]theme=([A-Za-z0-9_-]+)/.exec(s)?.[1];
    const raw: unknown = frag ? JSON.parse(fromB64(frag)) : s.startsWith("{") ? JSON.parse(s) : null;
    if (!raw || typeof raw !== "object") return null;
    const t = cleanTheme(raw, { id: "shared", by: typeof (raw as { by?: unknown }).by === "string" ? String((raw as { by: string }).by).slice(0, 40) : null, now: Date.now() });
    return typeof t === "string" ? null : shareOf(t);
  } catch {
    return null;
  }
}

/** As a theme the board can preview without keeping it. */
export function previewOf(share: ThemeShare): CustomTheme {
  return { ...share, id: "preview:shared", createdAt: 0, updatedAt: Date.now() };
}
